import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Input } from '../components/ui/input';
import {
  Trophy, TrendingUp, FileText, Scissors, CalendarCheck, Download, PhoneCall, Megaphone, Users,
} from 'lucide-react';
import { formatEUR, formatDate } from '../lib/api';
import { Button } from '../components/ui/button';
import MarcaLead from '../components/MarcaLead';
import RecuentoProcedencia from '../components/RecuentoProcedencia';
import RankingAgendasMes from '../components/RankingAgendasMes';

// Las columnas de marca del marcador de leads (26/9/2026): lo contactado de
// cada marca de la clínica (las manda el servidor con el marcador, en su
// orden), y lo que no se sabe de cuál es, aparte.
function columnasDeMarca(leads) {
  const marcas = Array.isArray(leads?.marcas) ? leads.marcas : [];
  return [...marcas.map((m) => m.clave), 'sin'];
}

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

// «Ana», «Ana y Berta», «Ana, Berta y Carla».
function enumerar(nombres) {
  const lista = (Array.isArray(nombres) ? nombres : []).filter(Boolean);
  if (lista.length <= 1) return lista.join('');
  return `${lista.slice(0, -1).join(', ')} y ${lista[lista.length - 1]}`;
}

// Quién sale en Indicadores (dirección, 04/10/2026): las personas marcadas
// en Usuarios. Lo dice el servidor con los comerciales; sin nadie marcado,
// salen todos y se dice dónde elegirlo.
function textoDelEquipo(equipo) {
  if (!equipo) return '';
  if (!equipo.elegido) {
    return 'Salen todos. Para elegir quién sale: Usuarios → Editar → «Sale en Indicadores».';
  }
  const n = Number(equipo.ya_no_estan) || 0;
  const fuera = n === 1 ? '1 que ya no está' : `${n} que ya no están`;
  const lista = [...(equipo.nombres || [])];
  if (n) lista.push(fuera);
  const nombres = enumerar(lista);
  return `Salen solo las personas marcadas en Usuarios («Sale en Indicadores»)${nombres ? `: ${nombres}` : ''}.`;
}

// Lo que se ha quedado fuera de los comerciales del mes por ser de quien no
// sale en Indicadores, dicho en una línea (vacío si no hay nada).
function fueraDeLosComerciales(fuera) {
  if (!fuera || typeof fuera !== 'object') return '';
  const trozos = [];
  if (fuera.presupuestos_entregados) trozos.push(plural(fuera.presupuestos_entregados, 'presupuesto', 'presupuestos'));
  if (fuera.cirugias_agendadas) trozos.push(plural(fuera.cirugias_agendadas, 'cirugía', 'cirugías'));
  if (fuera.primeras_valoraciones) {
    trozos.push(plural(fuera.primeras_valoraciones, 'primera valoración', 'primeras valoraciones'));
  }
  return trozos.join(' · ');
}

/**
 * Indicadores — objetivos comerciales por trabajador (solo administradores).
 * Quién entrega y envía presupuestos y, sobre todo, quién agenda cirugías:
 * al final de mes se recompensa al que más haya conseguido.
 */
export default function IndicadoresPage() {
  const [mes, setMes] = useState(() => new Date().toISOString().slice(0, 7));
  const [datos, setDatos] = useState(null);
  const [falloDatos, setFalloDatos] = useState('');
  // Leads contactados y por quién (dirección, 22/09/2026). Va aparte del resto:
  // el marcador de leads no es de un mes, es de toda la lista.
  const [leads, setLeads] = useState(null);
  const [falloLeads, setFalloLeads] = useState('');

  useEffect(() => {
    let off = false;
    setDatos(null);
    setFalloDatos('');
    api.get('/api/indicadores/comercial', { params: { mes } })
      .then((r) => { if (!off) { setDatos(r.data); setFalloDatos(''); } })
      // Un fallo de lectura NO es un mes sin ventas: dirección leía «sin
      // actividad» justo el mes de la recompensa (medido el 22/9).
      .catch((e) => {
        if (!off) { setDatos({ filas: [] }); setFalloDatos(e?.response?.data?.detail || 'no se ha podido consultar'); }
      });
    return () => { off = true; };
  }, [mes]);

  useEffect(() => {
    let off = false;
    api.get('/api/leads/marcador')
      .then((r) => { if (!off) { setLeads(r.data); setFalloLeads(''); } })
      // Un cero porque la consulta falló se leería como «no ha llamado nadie»:
      // se dice que no se ha podido leer.
      .catch((e) => { if (!off) setFalloLeads(e?.response?.data?.detail || 'no se ha podido leer'); });
    return () => { off = true; };
  }, []);

  const filas = datos?.filas || [];
  const detalle = datos?.detalle || [];
  const totales = datos?.totales || {};
  const [filtro, setFiltro] = useState('todo');   // todo | cirugia | presupuesto | primera_valoracion
  const lineas = filtro === 'todo' ? detalle : detalle.filter((d) => d.tipo === filtro);

  const ETIQUETA = {
    cirugia: 'Cirugía',
    presupuesto: 'Presupuesto',
    primera_valoracion: '1ª valoración',
  };

  const descargar = () => {
    const cab = ['Fecha', 'Tipo', 'Paciente', 'NIF', 'Detalle', 'Agendado por'];
    const filasCsv = lineas.map((d) => [
      d.fecha, ETIQUETA[d.tipo] || d.tipo, d.paciente, d.nif || '',
      d.referencia || '', d.trabajador,
    ]);
    const csv = [cab, ...filasCsv]
      .map((f) => f.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `indicadores_${mes}.csv`;
    a.click();
  };
  const maxCirugias = filas.length ? Math.max(...filas.map((f) => f.cirugias_agendadas)) : 0;
  // Quién sale y lo que se ha quedado fuera (04/10/2026). Un servidor viejo
  // no lo manda: entonces no se dice nada.
  const equipo = datos?.equipo || null;
  const fueraComerciales = fueraDeLosComerciales(datos?.fuera_del_equipo);
  // Cuántos contactados entran en el reparto por persona (con el conector,
  // con el asistente de WhatsApp y con lo de quien no sale en Indicadores,
  // que también van en su fila).
  const repartidos = (leads?.marcador || []).reduce((s, p) => s + (p.leads || 0), 0)
    + (leads?.por_el_bot || 0) + (leads?.por_el_asistente || 0) + (leads?.fuera_del_equipo || 0);
  // Contactados y pendientes de cada marca, sobre toda la lista. Un servidor
  // viejo no lo manda: entonces no hay columnas, en vez de enseñar ceros.
  const porMarca = leads?.por_marca || null;
  const MARCAS_MARCADOR = columnasDeMarca(leads);
  const marcasDelMarcador = Array.isArray(leads?.marcas) ? leads.marcas : [];
  // Los leads por procedencia (27/09/2026): las campañas de cada tratamiento y
  // las demás vías. Viene con el marcador; un servidor viejo no lo manda.
  const porProcedencia = leads?.por_procedencia?.filas ? leads.por_procedencia : null;

  return (
    <div>
      <PageHeader
        title="Indicadores"
        subtitle="Objetivos comerciales del mes, por trabajador. La cuenta de cirugías es por fecha de agendado."
      />

      <div className="cjo-card p-3 mb-4 flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-[#13231E]">Mes:</span>
        <Input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="w-44 h-9" data-testid="indicadores-mes" />
        {/* Quién sale (04/10/2026): discreto, al lado del mes. */}
        {equipo ? (
          <span className="text-xs text-muted-foreground min-w-0" data-testid="indicadores-equipo">
            <Users className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
            {textoDelEquipo(equipo)}
          </span>
        ) : null}
      </div>

      {/* Totalización del mes (2026-08-14): lo primero que se mira. */}
      {datos && filas.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="indicadores-totales">
          {[
            { k: 'primeras_valoraciones', label: '1ª valoraciones citadas', icono: CalendarCheck },
            { k: 'presupuestos_entregados', label: 'Presupuestos entregados', icono: FileText },
            { k: 'cirugias_agendadas', label: 'Cirugías agendadas', icono: Scissors, fuerte: true },
          ].map(({ k, label, icono: Ico, fuerte }) => (
            <div key={k} className={`cjo-card p-4 ${fuerte ? 'border-2 border-[#2E6E5E]/40' : ''}`}>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Ico className="w-3.5 h-3.5 text-[#2E6E5E]" /> {label}
              </div>
              <div className={`mt-1 tabular-nums font-bold ${fuerte ? 'text-3xl text-[#2E6E5E]' : 'text-2xl text-[#13231E]'}`}>
                {totales[k] ?? 0}
              </div>
            </div>
          ))}
          <div className="cjo-card p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <FileText className="w-3.5 h-3.5 text-[#2E6E5E]" /> Importe presupuestado
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums text-[#13231E]">
              {formatEUR(totales.importe_presupuestado || 0)}
            </div>
          </div>
        </div>
      )}

      <div className="cjo-card overflow-hidden">
        {datos === null ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : filas.length === 0 ? (
          falloDatos ? (
            <p className="text-sm text-red-700" data-testid="indicadores-fallo">
              No se ha podido consultar este mes ({falloDatos}). Esto no es un cero:
              vuelve a intentarlo.
            </p>
          ) : (
            <EmptyState icon={TrendingUp} title="Sin actividad"
              description={fueraComerciales
                ? 'Ninguna de las personas que salen en Indicadores tiene movimientos comerciales este mes.'
                : 'No hay movimientos comerciales en ese mes.'} />
          )
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                <tr>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E]">Trabajador</th>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E] text-right">Primeras valoraciones citadas</th>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E] text-right">Presupuestos entregados</th>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E] text-right">
                    {/* La copa de lucide, la misma que lleva quien va primero
                        (el emoji salía como un cuadrado; 5/10/2026). */}
                    <span className="inline-flex items-center gap-1.5">
                      Cirugías agendadas
                      <Trophy className="w-4 h-4 text-amber-500" aria-hidden="true" />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => {
                  const lider = maxCirugias > 0 && f.cirugias_agendadas === maxCirugias;
                  return (
                    <tr key={f.trabajador} className={`border-t border-border ${lider ? 'bg-amber-50/60 dark:bg-amber-950/20' : 'hover:bg-[rgba(46,110,94,0.03)]'}`} data-testid="indicadores-fila">
                      <td className="py-2.5 px-4 font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {lider && <Trophy className="w-4 h-4 text-amber-500" />}
                          {f.trabajador}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-right tabular-nums">{f.primeras_valoraciones}</td>
                      <td className="py-2.5 px-4 text-right tabular-nums">{f.presupuestos_entregados}</td>
                      <td className={`py-2.5 px-4 text-right tabular-nums font-bold ${lider ? 'text-amber-600' : ''}`}>
                        {f.cirugias_agendadas}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Lo de quien no sale en Indicadores (04/10/2026) no entra en las filas
          ni en los totales, pero no desaparece en silencio: se dice cuánto es. */}
      {fueraComerciales ? (
        <p className="text-xs text-muted-foreground mt-2" data-testid="indicadores-fuera">
          No entran, por ser de quien no sale en Indicadores: {fueraComerciales}.
        </p>
      ) : null}
      {/* DETALLE línea a línea: quién agendó qué y para qué paciente. */}
      {detalle.length > 0 && (
        <div className="cjo-card overflow-hidden mt-4" data-testid="indicadores-detalle">
          <div className="flex flex-wrap items-center gap-2 p-3 border-b border-border">
            <span className="text-sm font-semibold text-[#13231E]">Detalle del mes</span>
            <div className="flex gap-1">
              {[['todo', 'Todo'], ['cirugia', 'Cirugías'], ['presupuesto', 'Presupuestos'],
                ['primera_valoracion', '1ª valoraciones']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setFiltro(v)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium border ${
                    filtro === v ? 'bg-[#2E6E5E] text-white border-[#2E6E5E]'
                                 : 'bg-background text-muted-foreground border-border hover:text-foreground'}`}>
                  {l}
                </button>
              ))}
            </div>
            <span className="text-xs text-muted-foreground">{lineas.length} líneas</span>
            <Button size="sm" variant="outline" className="ml-auto h-8" onClick={descargar}
              data-testid="indicadores-descargar">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Excel
            </Button>
          </div>
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="w-full text-sm">
              <thead className="bg-[rgba(46,110,94,0.04)] text-left sticky top-0">
                <tr>
                  <th className="py-2 px-3 font-semibold text-[#13231E]">Fecha</th>
                  <th className="py-2 px-3 font-semibold text-[#13231E]">Tipo</th>
                  <th className="py-2 px-3 font-semibold text-[#13231E]">Paciente</th>
                  <th className="py-2 px-3 font-semibold text-[#13231E]">Detalle</th>
                  <th className="py-2 px-3 font-semibold text-[#13231E]">Agendado por</th>
                </tr>
              </thead>
              <tbody>
                {lineas.map((d, i) => (
                  <tr key={`${d.tipo}-${d.patient_id}-${i}`} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]"
                    data-testid="indicadores-linea">
                    <td className="py-2 px-3 text-xs tabular-nums text-muted-foreground whitespace-nowrap">
                      {formatDate(d.fecha)}
                    </td>
                    <td className="py-2 px-3">
                      <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                        d.tipo === 'cirugia' ? 'bg-[#2E6E5E] text-white'
                          : d.tipo === 'presupuesto' ? 'bg-[rgba(46,110,94,0.14)] text-[#13231E]'
                          : 'bg-muted text-muted-foreground'}`}>
                        {ETIQUETA[d.tipo] || d.tipo}
                      </span>
                    </td>
                    <td className="py-2 px-3 font-medium">
                      {d.paciente || '—'}
                      {d.nif && <span className="text-xs text-muted-foreground"> · {d.nif}</span>}
                    </td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">
                      {d.referencia}
                      {d.importe ? ` · ${formatEUR(d.importe)}` : ''}
                      {d.fecha_cirugia ? ` · opera el ${formatDate(d.fecha_cirugia)}` : ''}
                      {d.fecha_cita ? ` · cita el ${formatDate(d.fecha_cita)}` : ''}
                    </td>
                    <td className="py-2 px-3 font-medium whitespace-nowrap">{d.trabajador}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Rankings del mes por agenda (dirección, 1/10/2026): quién da más
          cirugías en cada quirófano y más consultas previas en cada consulta.
          Del mismo mes que lo de arriba. */}
      <RankingAgendasMes mes={mes} />

      {/* Leads contactados (dirección, 22/09/2026) */}
      <div className="mt-8" data-testid="indicadores-leads">
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
          <PhoneCall className="w-4 h-4" /> Leads contactados
        </h3>
        {falloLeads ? (
          <p className="text-sm text-red-700" data-testid="indicadores-leads-fallo">
            No se ha podido leer el marcador de leads ({falloLeads}). Esto no es un cero:
            es que no se ha podido consultar.
          </p>
        ) : !leads ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : (leads.marcador || []).length === 0 && !leads.por_el_bot && !leads.por_el_asistente
          && !leads.fuera_del_equipo ? (
          <p className="text-sm text-muted-foreground">
            Todavía no se ha contactado con ningún lead
            {leads.pendientes ? ` (${leads.pendientes} esperando)` : ''}.
            {porMarca && leads.pendientes ? (
              <span className="block text-xs mt-1" data-testid="indicadores-leads-esperando-marca">
                {[...marcasDelMarcador.map((m) => `${m.nombre}: ${porMarca[m.clave]?.pendientes ?? 0}`),
                  `Sin identificar: ${porMarca.sin?.pendientes ?? 0}`].join(' · ')}
              </span>
            ) : null}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className={`w-full text-sm ${porMarca ? 'max-w-3xl' : 'max-w-md'}`}>
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="py-2 pr-3">Quién</th>
                  <th className="py-2 pr-3 text-right">Contactados</th>
                  {/* Por marca (dirección, 26/9/2026): cuántos de cada una. */}
                  {porMarca ? MARCAS_MARCADOR.map((m) => (
                    <th key={m} className="py-2 pr-3 text-right" data-testid={`indicadores-leads-col-${m}`}>
                      <MarcaLead marca={m === 'sin' ? null : m} marcas={marcasDelMarcador} />
                    </th>
                  )) : null}
                </tr>
              </thead>
              <tbody>
                {/* La clave es la PERSONA (M15, 23/9/2026): con el nombre, dos
                    María García eran la misma fila para React. Quien ya no
                    está en la casa llega sin id y se queda con su nombre,
                    que es como el servidor la agrupa. */}
                {(leads.marcador || []).map((p) => (
                  <tr key={p.id || `nombre:${p.nombre}`} className="border-b last:border-0"
                    data-testid={`indicadores-leads-${p.id || p.nombre}`}>
                    <td className="py-2 pr-3">{p.nombre}</td>
                    <td className="py-2 pr-3 text-right tabular-nums font-medium">{p.leads}</td>
                    {porMarca ? MARCAS_MARCADOR.map((m) => (
                      <td key={m} className="py-2 pr-3 text-right tabular-nums"
                        data-testid={`indicadores-leads-${p.id || p.nombre}-${m}`}>
                        {leads.por_marca_por_persona?.[p.id || `nombre:${p.nombre}`]?.[m] ?? 0}
                      </td>
                    )) : null}
                  </tr>
                ))}
                {/* Lo que contactó el asistente de WhatsApp (1/11/2026): aparte
                    del equipo y del conector, con el nombre que manda el
                    servidor. No es de ninguna persona. */}
                {leads.por_el_asistente ? (
                  <tr className="text-xs text-muted-foreground" data-testid="indicadores-leads-asistente">
                    <td className="py-2 pr-3">{leads.nombre_asistente || 'El asistente de WhatsApp'} (no cuenta para nadie)</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{leads.por_el_asistente}</td>
                    {porMarca ? MARCAS_MARCADOR.map((m) => (
                      <td key={m} className="py-2 pr-3 text-right tabular-nums"
                        data-testid={`indicadores-leads-asistente-${m}`}>
                        {leads.por_el_asistente_por_marca?.[m] ?? 0}
                      </td>
                    )) : null}
                  </tr>
                ) : null}
                {leads.por_el_bot ? (
                  <tr className="text-xs text-muted-foreground" data-testid="indicadores-leads-bot">
                    <td className="py-2 pr-3">Marcados por el conector (no cuentan para nadie)</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{leads.por_el_bot}</td>
                    {porMarca ? MARCAS_MARCADOR.map((m) => (
                      <td key={m} className="py-2 pr-3 text-right tabular-nums">
                        {leads.por_el_bot_por_marca?.[m] ?? 0}
                      </td>
                    )) : null}
                  </tr>
                ) : null}
                {/* Lo contactado por quien no sale en Indicadores (04/10/2026):
                    aparte, como lo del conector. El total de abajo no cambia. */}
                {leads.fuera_del_equipo ? (
                  <tr className="text-xs text-muted-foreground" data-testid="indicadores-leads-fuera">
                    <td className="py-2 pr-3">De quien no sale en Indicadores</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{leads.fuera_del_equipo}</td>
                    {porMarca ? MARCAS_MARCADOR.map((m) => (
                      <td key={m} className="py-2 pr-3 text-right tabular-nums"
                        data-testid={`indicadores-leads-fuera-${m}`}>
                        {leads.fuera_del_equipo_por_marca?.[m] ?? 0}
                      </td>
                    )) : null}
                  </tr>
                ) : null}
                <tr className="text-xs text-muted-foreground">
                  <td className="py-2 pr-3">Total · {leads.pendientes} sin contactar</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{leads.contactados}</td>
                  {porMarca ? MARCAS_MARCADOR.map((m) => (
                    <td key={m} className="py-2 pr-3 text-right tabular-nums"
                      data-testid={`indicadores-leads-total-${m}`}>
                      {porMarca[m]?.contactados ?? 0}
                    </td>
                  )) : null}
                </tr>
                {porMarca ? (
                  <tr className="text-xs text-muted-foreground" data-testid="indicadores-leads-pendientes-marca">
                    <td className="py-2 pr-3">Sin contactar</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{leads.pendientes}</td>
                    {MARCAS_MARCADOR.map((m) => (
                      <td key={m} className="py-2 pr-3 text-right tabular-nums"
                        data-testid={`indicadores-leads-pendientes-${m}`}>
                        {porMarca[m]?.pendientes ?? 0}
                      </td>
                    ))}
                  </tr>
                ) : null}
              </tbody>
            </table>
            {/* Si el reparto no cabe entero se DICE, como en la lista de
                Leads (M15): un marcador cortado en silencio se lee como el
                marcador entero. El total de abajo sí es el de toda la lista. */}
            {/* De los contactados, cuántos ya tienen cita (27/09/2026): esos
                ya no están en la lista de Leads, pero el punto es de quien
                llamó. */}
            {leads.ya_citados ? (
              <p className="text-xs text-muted-foreground mt-2" data-testid="indicadores-leads-ya-citados">
                De los {leads.contactados} contactados, {leads.ya_citados} ya tienen cita.
              </p>
            ) : null}
            {leads.truncado ? (
              <p className="text-xs text-amber-700 mt-2" data-testid="indicadores-leads-truncado">
                El reparto por persona cuenta los {repartidos} contactados más recientes
                de {leads.contactados}; el total sí es el de toda la lista.
              </p>
            ) : null}
          </div>
        )}
      </div>

      {/* Leads por procedencia (dirección, 27/09/2026: «para luego tener un buen
          recuento de qué hacemos con cada cosa»). */}
      {porProcedencia ? (
        <div className="mt-8" data-testid="indicadores-procedencia">
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
            <Megaphone className="w-4 h-4" /> Leads por procedencia
          </h3>
          {porProcedencia.filas.length === 0 ? (
            <p className="text-sm text-muted-foreground">Todavía no hay leads.</p>
          ) : (
            <RecuentoProcedencia filas={porProcedencia.filas} totales={porProcedencia.totales}
              testid="indicadores-procedencia-tabla" />
          )}
          {porProcedencia.completo === false ? (
            <p className="text-xs text-amber-700 mt-2" data-testid="indicadores-procedencia-cortado">
              Hay más leads de los que se pueden contar de una vez: estos números no son de toda la lista.
            </p>
          ) : null}
        </div>
      ) : null}

      <p className="text-xs text-muted-foreground mt-3">
        «Primeras valoraciones» son las consultas previas (presenciales y online) que cada
        persona ha CITADO ese mes, no las que se atienden: cuenta cuándo se consigue la cita.
        No entran las primeras revisiones, que son curas de después de operar.
        «Entregados» cuenta los presupuestos creados por cada persona ese mes. Lo anulado no cuenta.
        <br />
        El detalle no se teclea: el paciente y quien lo agendó salen de lo que guarda el
        programa al crear cada cita o presupuesto, así que no se puede apuntar algo que no
        existe ni ponerlo a nombre de otra persona.
        <br />
        «Leads contactados» cuenta los contactos de Leads/Contactos que alguien ha marcado
        como atendidos, y sale de quién pulsó el botón: tampoco se teclea. La marca de cada
        uno sale del primer correo de una marca al que escribió; si no, de «de dónde
        viene» (así se sabe también la procedencia externa). Lo que no se sabe va en «Sin identificar»
        y no se reparte. Los contactos descartados no cuentan, y quien ya tiene cita
        no cuenta como «sin contactar».
        <br />
        «Leads por procedencia» dice de dónde viene cada contacto: la campaña de su
        tratamiento (la del formulario de Meta, lo que diga «de dónde viene» o lo puesto a
        mano en Leads), Web, Instagram, TikTok u «Otras», que dice debajo lo que lleva
        (llamadas, recomendaciones…). «Recibidos» son todos los que han llegado,
        también los descartados (que se ven aparte) y los ya citados. Pulsa una para ver sus contactos.
        <br />
        Quién sale: las personas marcadas en Usuarios («Sale en Indicadores»), sigan o no en la
        casa. Lo de las demás no entra en las filas ni en los totales y se dice aparte; en «Leads
        contactados» el total y los «sin contactar» siguen siendo los de toda la lista. Si no hay
        nadie marcado, salen todos.
      </p>
    </div>
  );
}
